"use client";
import { useRadio } from "../radio/radio.js";
import { MenuItemCheckedContext, useMenuScopedContext } from "./menu-context.js";
import { useMenuItem } from "./menu-item.js";
import { createElement, createHook, forwardRef, memo, useInitialValue, useWrapElement } from "@ariakit/react-utils";
import { invariant } from "@ariakit/utils";
import { useEffect } from "react";
import { jsx } from "react/jsx-runtime";
import { useStoreState } from "@ariakit/react-store";
//#region src/menu/menu-item-radio.tsx
const TagName = "div";
function getValue(prevValue, value, checked) {
	if (checked === void 0) return prevValue;
	if (checked) return value;
	return prevValue === value ? false : prevValue;
}
/**
* Returns props to create a `MenuItemRadio` component.
* @see https://ariakit.com/components/menu
* @example
* ```jsx
* const store = useMenuStore({ defaultValues: { fruit: "apple" } });
* const apple = useMenuItemRadio({ store, name: "fruit", value: "apple" });
* const orange = useMenuItemRadio({ store, name: "fruit", value: "orange" });
* <MenuButton store={store}>Fruit</MenuButton>
* <Menu store={store}>
*   <Role {...apple}>Apple</Role>
*   <Role {...orange}>Orange</Role>
* </Menu>
* ```
*/
const useMenuItemRadio = createHook(function useMenuItemRadio({ store, name, value, checked, defaultChecked: defaultCheckedProp, onChange: onChangeProp, hideOnClick = false, ...props }) {
	const context = useMenuScopedContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "MenuItemRadio must be wrapped in a MenuList or Menu component");
	const defaultChecked = useInitialValue(defaultCheckedProp);
	useEffect(() => {
		store?.setValue(name, (prevValue = false) => {
			return getValue(prevValue, value, defaultChecked);
		});
	}, [
		store,
		name,
		value,
		defaultChecked
	]);
	useEffect(() => {
		if (checked === void 0) return;
		store?.setValue(name, (prevValue) => {
			return getValue(prevValue, value, checked);
		});
	}, [
		store,
		name,
		value,
		checked
	]);
	const isChecked = useStoreState(store, (state) => state.values[name] === value);
	props = useWrapElement(props, (element) => /* @__PURE__ */ jsx(MenuItemCheckedContext.Provider, {
		value: isChecked,
		children: element
	}), [isChecked]);
	props = {
		role: "menuitemradio",
		...props
	};
	props = useRadio({
		name,
		value,
		checked: isChecked,
		onChange(event) {
			onChangeProp?.(event);
			if (event.defaultPrevented) return;
			const element = event.currentTarget;
			store?.setValue(name, (prevValue) => {
				return getValue(prevValue, value, checked ?? element.checked);
			});
		},
		...props
	});
	props = useMenuItem({
		store,
		hideOnClick,
		...props
	});
	return props;
});
/**
* Renders a [`menuitemradio`](https://w3c.github.io/aria/#menuitemradio)
* element within a [`Menu`](https://ariakit.com/reference/menu) component. The
* [`name`](https://ariakit.com/reference/menu-item-radio#name) prop must be
* provided to identify the field in the
* [`values`](https://ariakit.com/reference/menu-provider#values) state.
*
* A [`MenuItemCheck`](https://ariakit.com/reference/menu-item-check) can be
* used to render a checkmark inside this component.
* @see https://ariakit.com/components/menu
* @example
* ```jsx {4-11}
* <MenuProvider defaultValues={{ profile: "john" }}>
*   <MenuButton>Profiles</MenuButton>
*   <Menu>
*     <MenuItemRadio name="profile" value="john">
*       <MenuItemCheck />
*       John Doe
*     </MenuItemRadio>
*     <MenuItemRadio name="profile" value="jane">
*       <MenuItemCheck />
*       Jane Doe
*     </MenuItemRadio>
*   </Menu>
* </MenuProvider>
* ```
*/
const MenuItemRadio = memo(forwardRef(function MenuItemRadio(props) {
	return createElement(TagName, useMenuItemRadio(props));
}));
//#endregion
export { MenuItemRadio, useMenuItemRadio };

//# sourceMappingURL=menu-item-radio.js.map